import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ChemicalProfile = ({ chemical, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'Info' },
    { id: 'hazards', label: 'Hazards & Safety', icon: 'AlertTriangle' },
    { id: 'physical', label: 'Physical Properties', icon: 'Beaker' },
    { id: 'exposure', label: 'Exposure Limits', icon: 'Activity' },
    { id: 'reactivity', label: 'Reactivity Data', icon: 'Zap' },
    { id: 'documents', label: 'Documents', icon: 'FileText' }
  ];

  const getHazardColor = (hazard) => {
    const colorMap = {
      Flammable: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
      Toxic: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
      Corrosive: 'bg-red-500/10 text-red-500 border-red-500/20',
      Carcinogen: 'bg-destructive/10 text-destructive border-destructive/20',
      Irritant: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      Reactive: 'bg-blue-500/10 text-blue-500 border-blue-500/20'
    };
    return colorMap?.[hazard] || 'bg-muted text-foreground border-border';
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-1">{chemical?.name}</h2>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>CAS: {chemical?.casNumber}</span>
              <span>Formula: {chemical?.formula}</span>
              <span>State: {chemical?.physicalState}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>
        
        <div className="flex flex-wrap gap-2">
          {chemical?.hazardClass?.map((hazard, idx) => (
            <span
              key={idx}
              className={`text-sm px-3 py-1 rounded border ${getHazardColor(hazard)}`}
            >
              {hazard}
            </span>
          ))}
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex overflow-x-auto">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              {tab?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Chemical Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-muted-foreground">CAS Number</span>
                  <p className="text-foreground font-medium">{chemical?.casNumber}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Molecular Formula</span>
                  <p className="text-foreground font-medium font-mono">{chemical?.formula}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Physical State</span>
                  <p className="text-foreground font-medium">{chemical?.physicalState}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Regulatory Status</span>
                  <p className="text-foreground font-medium">{chemical?.regulatoryStatus}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">MSDS Version</span>
                  <p className="text-foreground font-medium">{chemical?.msdsVersion}</p>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Last Updated</span>
                  <p className="text-foreground font-medium">{chemical?.lastUpdated}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'hazards' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">Hazard Classifications</h3>
              <div className="flex flex-wrap gap-2 mb-6">
                {chemical?.hazardClass?.map((hazard, idx) => (
                  <span
                    key={idx}
                    className={`px-4 py-2 rounded border ${getHazardColor(hazard)}`}
                  >
                    {hazard}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertTriangle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Safety Warnings</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Keep away from heat, sparks, and open flames</li>
                    <li>• Use in well-ventilated areas only</li>
                    <li>• Wear appropriate PPE including gloves and eye protection</li>
                    <li>• Avoid skin and eye contact</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'physical' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-3">Physical Properties</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Flash Point</span>
                <p className="text-xl font-bold text-foreground mt-1">{chemical?.flashPoint}</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Autoignition Temperature</span>
                <p className="text-xl font-bold text-foreground mt-1">{chemical?.autoignitionTemp}</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Lower Flammability Limit</span>
                <p className="text-xl font-bold text-foreground mt-1">{chemical?.flammabilityLimits?.lower}</p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Upper Flammability Limit</span>
                <p className="text-xl font-bold text-foreground mt-1">{chemical?.flammabilityLimits?.upper}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'exposure' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-3">Exposure Limits</h3>
            <div className="space-y-3">
              <div className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">TWA (Time Weighted Average)</span>
                  <span className="text-lg font-bold text-foreground">{chemical?.exposure?.twa}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-success h-2 rounded-full" style={{ width: '60%' }}></div>
                </div>
              </div>
              
              <div className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">STEL (Short Term Exposure Limit)</span>
                  <span className="text-lg font-bold text-foreground">{chemical?.exposure?.stel}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-warning h-2 rounded-full" style={{ width: '75%' }}></div>
                </div>
              </div>
              
              <div className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Ceiling Limit</span>
                  <span className="text-lg font-bold text-foreground">{chemical?.exposure?.ceiling}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-destructive h-2 rounded-full" style={{ width: '90%' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reactivity' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-3">Reactivity & Incompatibility</h3>
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Incompatible Materials</h4>
                  <p className="text-sm text-muted-foreground">
                    Avoid contact with strong oxidizers, acids, and bases. May react violently with certain materials.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="p-3 border border-border rounded-lg flex items-center justify-between">
                <span className="text-sm text-foreground">Oxidizing Agents</span>
                <span className="text-xs px-2 py-1 bg-destructive/10 text-destructive border border-destructive/20 rounded">
                  Incompatible
                </span>
              </div>
              <div className="p-3 border border-border rounded-lg flex items-center justify-between">
                <span className="text-sm text-foreground">Strong Acids</span>
                <span className="text-xs px-2 py-1 bg-warning/10 text-warning border border-warning/20 rounded">
                  Caution
                </span>
              </div>
              <div className="p-3 border border-border rounded-lg flex items-center justify-between">
                <span className="text-sm text-foreground">Bases</span>
                <span className="text-xs px-2 py-1 bg-success/10 text-success border border-success/20 rounded">
                  Compatible
                </span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-3">Associated Documents</h3>
            <div className="space-y-2">
              {[
                { name: 'Safety Data Sheet (SDS)', version: 'Rev 4.0', date: '12/01/2025' },
                { name: 'Reactivity Study Report', version: 'Rev 2.1', date: '11/15/2025' },
                { name: 'Handling Procedures', version: 'Rev 1.8', date: '10/28/2025' }
              ]?.map((doc, idx) => (
                <div key={idx} className="p-4 border border-border rounded-lg flex items-center justify-between hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-3">
                    <Icon name="FileText" size={20} className="text-primary" />
                    <div>
                      <p className="font-medium text-foreground">{doc?.name}</p>
                      <p className="text-xs text-muted-foreground">{doc?.version} • {doc?.date}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                      <Icon name="Eye" size={16} className="text-muted-foreground" />
                    </button>
                    <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                      <Icon name="Download" size={16} className="text-muted-foreground" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Footer Actions */}
      <div className="p-6 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium">
            <Icon name="Link" size={16} />
            Link to P&ID
          </button>
          <div className="flex gap-2">
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium">
              <Icon name="Edit" size={16} />
              Edit
            </button>
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
              <Icon name="Download" size={16} />
              Download MSDS
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChemicalProfile;